import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  REGIONS,
  simulateRules,
  type SimulateResponse,
  type SimulatedBiomarker,
  type SimulatedPatient,
} from "@/api/authoring";
import { useTenantScope } from "@/auth/tenant";
import { AppLayout } from "@/components/AppLayout";
import { TenantField } from "@/components/TenantField";
import { BiomarkerCombobox } from "@/components/BiomarkerCombobox";
import { t } from "@/i18n";

/**
 * Simulation runner (phase2-admin-ui PRD §Simulation runner).
 *
 * Build a synthetic test case (biomarker readings in canonical units +
 * profile + conditions/medications), optionally paste a draft rule (JSON),
 * and run it against the REAL rule engine via the authoring BFF. Shows the
 * fired insights, the baseline-vs-draft diff, and the resolved per-biomarker
 * readings — so an author inspects a change's effect before opening the MR.
 */

/** One patient's outcome in a batch run — either a result or an error string. */
interface BatchRow {
  patientId: string;
  result?: SimulateResponse;
  error?: string;
}

export function SimulatePage(): JSX.Element {
  const [params] = useSearchParams();
  const [region, setRegion] = useState(params.get("region") ?? "EU");
  const [orgInput, setOrgInput] = useState(params.get("org") ?? "");
  const { org, pinned } = useTenantScope(orgInput);
  const [rows, setRows] = useState<SimulatedBiomarker[]>([{ name: "", value: 0, unit: "" }]);
  const [gender, setGender] = useState("");
  const [age, setAge] = useState<number | string>("");
  const [conditions, setConditions] = useState("");
  const [medications, setMedications] = useState("");
  const [draftRule, setDraftRule] = useState("");

  const simulation = useMutation<SimulateResponse, Error, void>({
    mutationFn: () => {
      let draft: Record<string, unknown> | null = null;
      if (draftRule.trim() !== "") {
        draft = JSON.parse(draftRule) as Record<string, unknown>;
      }
      const profile: Record<string, string | number> = {};
      if (gender !== "") profile.gender = gender;
      if (age !== "" && typeof age === "number") profile.age = age;
      return simulateRules({
        region,
        organization_id: org || null,
        draft_rule: draft,
        patient: {
          biomarkers: rows.filter((r) => r.name.trim() !== ""),
          profile,
          conditions: conditions
            .split(",")
            .map((c) => c.trim())
            .filter((c) => c !== ""),
          medications: medications
            .split(",")
            .map((m) => m.trim())
            .filter((m) => m !== ""),
        },
      });
    },
  });

  const draftIsInvalid = ((): boolean => {
    if (draftRule.trim() === "") return false;
    try {
      JSON.parse(draftRule);
      return false;
    } catch {
      return true;
    }
  })();

  // Batch: run the same region / org / draft against N patients (a JSON array),
  // reusing the single-patient endpoint per patient. allSettled so one bad patient
  // doesn't sink the whole run — its row shows the error instead.
  const [batchInput, setBatchInput] = useState("");
  const batch = useMutation<BatchRow[], Error, void>({
    mutationFn: async () => {
      const parsed = JSON.parse(batchInput) as unknown;
      if (!Array.isArray(parsed)) throw new Error(t("authoring.sim_batch_invalid"));
      const patients = parsed as SimulatedPatient[];
      let draft: Record<string, unknown> | null = null;
      if (draftRule.trim() !== "") draft = JSON.parse(draftRule) as Record<string, unknown>;
      const settled = await Promise.allSettled(
        patients.map((patient) =>
          simulateRules({ region, organization_id: org || null, draft_rule: draft, patient }),
        ),
      );
      return settled.map((outcome, i) => ({
        patientId: patients[i]?.patient_id ?? `#${i + 1}`,
        ...(outcome.status === "fulfilled"
          ? { result: outcome.value }
          : {
              error:
                outcome.reason instanceof Error
                  ? outcome.reason.message
                  : String(outcome.reason),
            }),
      }));
    },
  });
  const batchInvalid = ((): boolean => {
    if (batchInput.trim() === "") return true;
    try {
      return !Array.isArray(JSON.parse(batchInput));
    } catch {
      return true;
    }
  })();
  const batchRows = batch.data;
  const completed =
    batchRows?.filter((r): r is BatchRow & { result: SimulateResponse } => r.result !== undefined) ??
    [];
  const avgMs =
    completed.length > 0
      ? completed.reduce((sum, r) => sum + r.result.elapsed_ms, 0) / completed.length
      : null;

  const setRow = (index: number, patch: Partial<SimulatedBiomarker>): void => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const result = simulation.data;

  return (
    <AppLayout breadcrumbs={[{ label: t("authoring.simulate_title") }]}>
      <Title order={1}>{t("authoring.simulate_title")}</Title>
      <Text c="dimmed" mt="xs">
        {t("authoring.simulate_intro")}
      </Text>

      <Group mt="md" align="flex-end">
        <Select
          label={t("authoring.region")}
          data={[...REGIONS]}
          value={region}
          allowDeselect={false}
          onChange={(v) => {
            if (v !== null) setRegion(v);
          }}
        />
        <TenantField org={org} pinned={pinned} onChange={setOrgInput} />
      </Group>

      <Title order={3} mt="lg">
        {t("authoring.sim_case_title")}
      </Title>
      <Stack gap="xs" mt="xs">
        {rows.map((row, index) => (
          // eslint-disable-next-line react/no-array-index-key -- positional editor rows
          <Group key={index} align="flex-end">
            <BiomarkerCombobox
              label={index === 0 ? t("authoring.sim_biomarker") : undefined}
              aria-label={t("authoring.sim_biomarker")}
              placeholder="HBA1C"
              uppercase
              value={row.name}
              onChange={(name) => setRow(index, { name })}
            />
            <NumberInput
              label={index === 0 ? t("authoring.sim_value") : undefined}
              value={row.value}
              onChange={(v) => setRow(index, { value: typeof v === "number" ? v : 0 })}
            />
            <TextInput
              label={index === 0 ? t("authoring.sim_unit") : undefined}
              placeholder="%"
              value={row.unit}
              onChange={(e) => setRow(index, { unit: e.currentTarget.value })}
            />
            <ActionIcon
              variant="subtle"
              color="red"
              aria-label={t("authoring.sim_remove_row")}
              onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
            >
              ✕
            </ActionIcon>
          </Group>
        ))}
        <Group>
          <Button
            variant="light"
            size="xs"
            onClick={() => setRows((prev) => [...prev, { name: "", value: 0, unit: "" }])}
          >
            {t("authoring.sim_add_row")}
          </Button>
        </Group>
        <Group>
          <Select
            label={t("authoring.sim_gender")}
            data={["male", "female"]}
            value={gender === "" ? null : gender}
            clearable
            onChange={(v) => setGender(v ?? "")}
          />
          <NumberInput label={t("authoring.sim_age")} value={age} onChange={setAge} min={0} />
          <TextInput
            label={t("authoring.sim_conditions")}
            placeholder="diabetes, hypertension"
            value={conditions}
            onChange={(e) => setConditions(e.currentTarget.value)}
            style={{ flexGrow: 1 }}
          />
          <TextInput
            label={t("authoring.sim_medications")}
            placeholder="statin"
            value={medications}
            onChange={(e) => setMedications(e.currentTarget.value)}
            style={{ flexGrow: 1 }}
          />
        </Group>
        <Textarea
          label={t("authoring.sim_draft_label")}
          description={t("authoring.sim_draft_desc")}
          autosize
          minRows={4}
          value={draftRule}
          onChange={(e) => setDraftRule(e.currentTarget.value)}
          styles={{ input: { fontFamily: "monospace" } }}
        />
        {draftIsInvalid && <Alert color="red">{t("authoring.draft_invalid_json")}</Alert>}
        <Group>
          <Button
            loading={simulation.isPending}
            disabled={draftIsInvalid}
            onClick={() => simulation.mutate()}
          >
            {t("authoring.sim_run")}
          </Button>
        </Group>
      </Stack>

      {simulation.isError && (
        <Alert color="red" mt="md" title={t("authoring.error_title")}>
          {simulation.error.message}
        </Alert>
      )}

      {result !== undefined && (
        <Stack mt="lg">
          <Text size="sm" c="dimmed">
            {t("authoring.sim_elapsed")}: {result.elapsed_ms} ms
          </Text>
          {Object.keys(result.resolved_symbols).length > 0 && (
            <Text size="sm" c="dimmed">
              {t("authoring.sim_resolved_prefix")}{" "}
              {Object.entries(result.resolved_symbols)
                .map(([typed, symbol]) => `${typed} → ${symbol}`)
                .join(" · ")}
            </Text>
          )}
          {result.unknown_biomarkers.length > 0 && (
            <Alert color="yellow" title={t("authoring.sim_unknown_title")}>
              {result.unknown_biomarkers.join(", ")}
            </Alert>
          )}
          {/*
            RED, not yellow, and not for decoration. An unknown symbol is an author
            typo with no clinical consequence -- the row simply is not evaluated. A
            wrong UNIT is the case that used to produce a confident, fabricated band,
            because the engine does not string-compare a single-unit bucket group. The
            two deserve different colours.
          */}
          {/*
            `?? []` because the SPA and the API are separate deploy surfaces. A UI
            built against this field, served against an API that predates it, would
            otherwise throw on `undefined.length` and blank the whole results panel --
            the same Fly-vs-elsewhere skew that hid a wrong SR_AUDIT_URL for weeks.
          */}
          {(result.unrecognized_units ?? []).length > 0 && (
            <Alert color="red" title={t("authoring.sim_bad_unit_title")}>
              {(result.unrecognized_units ?? [])
                .map(
                  (u) =>
                    `${u.biomarker}: ${u.unit} (${t("authoring.sim_bad_unit_declared")} ${
                      u.declared_units.join(", ") || "—"
                    })`,
                )
                .join(" · ")}
            </Alert>
          )}
          <Group align="stretch" grow>
            <Card withBorder>
              <Title order={4}>{t("authoring.sim_baseline")}</Title>
              <Text size="sm">
                {result.baseline.rules_fired} / {result.baseline.rules_evaluated}{" "}
                {t("authoring.sim_fired_suffix")}
              </Text>
              <Text size="sm" style={{ wordBreak: "break-word" }}>
                {result.baseline.fired_insight_ids.join(", ") || t("authoring.sim_none")}
              </Text>
            </Card>
            {result.with_draft !== null && (
              <Card withBorder>
                <Title order={4}>{t("authoring.sim_with_draft")}</Title>
                <Text size="sm">
                  {result.with_draft.rules_fired} / {result.with_draft.rules_evaluated}{" "}
                  {t("authoring.sim_fired_suffix")}
                </Text>
                <Text size="sm" style={{ wordBreak: "break-word" }}>
                  {result.with_draft.fired_insight_ids.join(", ") || t("authoring.sim_none")}
                </Text>
                <Group mt="xs" gap="xs">
                  {result.draft_rule_fired !== null && (
                    <Badge color={result.draft_rule_fired ? "green" : "gray"} variant="light">
                      {result.draft_rule_fired
                        ? t("authoring.sim_draft_fired")
                        : t("authoring.sim_draft_not_fired")}
                    </Badge>
                  )}
                  {result.added_insight_ids.map((id) => (
                    <Badge key={id} color="green" variant="light">
                      + {id}
                    </Badge>
                  ))}
                  {result.removed_insight_ids.map((id) => (
                    <Badge key={id} color="red" variant="light">
                      − {id}
                    </Badge>
                  ))}
                </Group>
              </Card>
            )}
          </Group>
          {result.biomarker_readings.length > 0 && (
            <Table withTableBorder striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("authoring.sim_biomarker")}</Table.Th>
                  <Table.Th>{t("authoring.sim_reading")}</Table.Th>
                  <Table.Th>{t("authoring.col_severity")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {result.biomarker_readings.map((reading) => (
                  <Table.Tr key={reading.biomarker}>
                    <Table.Td>{reading.biomarker}</Table.Td>
                    <Table.Td>{reading.reading}</Table.Td>
                    <Table.Td>{reading.severity ?? "—"}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      )}

      <Stack mt="xl">
        <Title order={3}>{t("authoring.sim_batch_title")}</Title>
        <Text size="sm" c="dimmed">
          {t("authoring.sim_batch_help")}
        </Text>
        <Textarea
          value={batchInput}
          onChange={(e) => setBatchInput(e.currentTarget.value)}
          autosize
          minRows={4}
          placeholder='[{"patient_id": "p1", "biomarkers": [{"name": "GLU", "value": 200, "unit": "mg/dL"}]}]'
          styles={{ input: { fontFamily: "monospace" } }}
        />
        {batchInput.trim() !== "" && batchInvalid && (
          <Alert color="red">{t("authoring.sim_batch_invalid")}</Alert>
        )}
        <Group>
          <Button
            loading={batch.isPending}
            disabled={batchInvalid || draftIsInvalid}
            onClick={() => batch.mutate()}
          >
            {t("authoring.sim_batch_run")}
          </Button>
        </Group>
        {batch.isError && (
          <Alert color="red" title={t("authoring.error_title")}>
            {batch.error.message}
          </Alert>
        )}
        {batchRows !== undefined && batchRows.length > 0 && (
          <>
            {avgMs !== null && (
              <Text size="sm" c="dimmed">
                {t("authoring.sim_batch_avg")}: {avgMs.toFixed(1)} ms
              </Text>
            )}
            <Table withTableBorder striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("authoring.sim_batch_col_patient")}</Table.Th>
                  <Table.Th>{t("authoring.sim_batch_col_fired")}</Table.Th>
                  <Table.Th>{t("authoring.sim_batch_col_diff")}</Table.Th>
                  <Table.Th>{t("authoring.sim_batch_col_ms")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {batchRows.map((row, i) => (
                  <Table.Tr key={`${row.patientId}-${i}`}>
                    <Table.Td>{row.patientId}</Table.Td>
                    {row.result !== undefined ? (
                      <>
                        <Table.Td>
                          {row.result.baseline.rules_fired}
                          {row.result.with_draft !== null
                            ? ` / ${row.result.with_draft.rules_fired}`
                            : ""}
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4}>
                            {row.result.added_insight_ids.map((id) => (
                              <Badge key={`a-${id}`} color="green" variant="light">
                                + {id}
                              </Badge>
                            ))}
                            {row.result.removed_insight_ids.map((id) => (
                              <Badge key={`r-${id}`} color="red" variant="light">
                                − {id}
                              </Badge>
                            ))}
                          </Group>
                        </Table.Td>
                        <Table.Td>{row.result.elapsed_ms}</Table.Td>
                      </>
                    ) : (
                      <Table.Td colSpan={3}>
                        <Text c="red" size="sm">
                          {t("authoring.sim_batch_row_error")}: {row.error}
                        </Text>
                      </Table.Td>
                    )}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </>
        )}
      </Stack>
    </AppLayout>
  );
}
